import { ParsedQs } from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useNamespace } from '../components/NamespaceProvider';

import { parseSearch, stringifyQuery } from './queryString';

export { parseSearch, stringifyQuery } from './queryString';

export const useSearch = <T extends Record<string, unknown> = ParsedQs>({
  namespacing = true,
}: {
  namespacing?: boolean;
} = {}): T => {
  const namespace = useNamespace();
  const location = useLocation();
  const { search } = location;

  return useMemo(() => {
    const parsedSearch = parseSearch<T>(search);

    if (namespace && namespacing) {
      return (namespace in parsedSearch ? parsedSearch[namespace] : {}) as T;
    }

    return parsedSearch;
  }, [namespacing, namespace, search]);
};

export const useUpdateSearch = <T extends Record<string, unknown> = ParsedQs>({
  namespacing = true,
}: {
  namespacing?: boolean;
} = {}): ((query: Record<string, unknown>) => void) => {
  const history = useHistory();
  const namespace = useNamespace();
  const currentQuery = useSearch<T>();

  return useCallback(
    (query, { replace = false } = {}) => {
      let search;

      if (namespace && namespacing) {
        search = stringifyQuery({
          ...parseSearch(history.location.search),
          [namespace]: {
            ...currentQuery,
            ...query,
          },
        });
      } else {
        search = stringifyQuery({
          ...parseSearch(history.location.search),
          ...query,
        });
      }

      if (replace) {
        history.replace({
          ...location,
          search,
        });
      } else {
        history.push({
          ...location,
          search,
        });
      }
    },
    [currentQuery, history, namespace, namespacing],
  );
};

export const useSearchQuery = <T extends Record<string, unknown> = ParsedQs>({
  namespacing = true,
}: {
  namespacing?: boolean;
} = {}): {
  searchQuery: Partial<T>;
  updateSearchQuery: (query: Record<string, unknown>) => void;
} => {
  const searchQuery = useSearch<T>({ namespacing });

  const updateSearchQuery = useUpdateSearch<T>({ namespacing });

  return useMemo(
    () => ({
      searchQuery,
      updateSearchQuery,
    }),
    [searchQuery, updateSearchQuery],
  );
};
